import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Camera, ArrowRight, Moon, Sun } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_da191abd-9276-45a7-bd48-1757c1f4e471/artifacts/p5urxzpf_diametor_logo_black__1_-removebg-preview.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_debt-cycle/artifacts/gzyrfqiy_diametor_logo_white-removebg-preview.png";

const COUNTRY_CODES = [
  { code: '+91', country: 'IN', label: 'India (+91)' },
  { code: '+1', country: 'US', label: 'USA (+1)' },
  { code: '+44', country: 'GB', label: 'UK (+44)' },
  { code: '+971', country: 'AE', label: 'UAE (+971)' },
  { code: '+65', country: 'SG', label: 'Singapore (+65)' },
  { code: '+61', country: 'AU', label: 'Australia (+61)' },
  { code: '+49', country: 'DE', label: 'Germany (+49)' },
  { code: '+33', country: 'FR', label: 'France (+33)' },
  { code: '+81', country: 'JP', label: 'Japan (+81)' },
  { code: '+86', country: 'CN', label: 'China (+86)' },
];

const CURRENCIES = [
  { code: 'INR', symbol: '\u20B9', label: 'Indian Rupee (INR)', enabled: true },
  { code: 'USD', symbol: '$', label: 'US Dollar (USD)', enabled: false },
  { code: 'EUR', symbol: '\u20AC', label: 'Euro (EUR)', enabled: false },
  { code: 'GBP', symbol: '\u00A3', label: 'British Pound (GBP)', enabled: false },
  { code: 'AED', symbol: 'AED', label: 'UAE Dirham (AED)', enabled: false },
  { code: 'SGD', symbol: 'S$', label: 'Singapore Dollar (SGD)', enabled: false },
  { code: 'AUD', symbol: 'A$', label: 'Australian Dollar (AUD)', enabled: false },
  { code: 'JPY', symbol: '\u00A5', label: 'Japanese Yen (JPY)', enabled: false },
];

export default function ProfileCreation() {
  const { user, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const isDark = theme === 'dark';

  const [name, setName] = useState(user?.name || '');
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [photoPreview, setPhotoPreview] = useState(user?.picture || null);
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!phone.trim()) { toast.error('Phone number is required'); return; }
    if (!currency) { toast.error('Currency is required'); return; }
    setSaving(true);
    try {
      if (photoFile) {
        const formData = new FormData();
        formData.append('file', photoFile);
        await axios.post(`${API}/profile/upload-photo`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      await axios.put(`${API}/profile`, {
        name: name.trim(),
        phone: `${countryCode}${phone.trim()}`,
        country_code: countryCode,
        currency,
        profile_complete: true
      }, { withCredentials: true });
      await refreshUser();
      toast.success('Profile created!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-white'}`} data-testid="profile-creation-page">
      {/* Theme toggle - floating */}
      <button
        onClick={toggleTheme}
        className={`fixed top-4 right-4 z-50 p-2.5 rounded-sm border transition-colors ${isDark ? 'border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-yellow-400' : 'border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-600 hover:text-yellow-500'}`}
        data-testid="profile-theme-toggle"
      >
        {isDark ? <Sun size={18} weight="bold" /> : <Moon size={18} weight="bold" />}
      </button>

      {/* Header */}
      <div className={`py-4 px-6 border-b ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
        <img 
          src={isDark ? LOGO_DARK : LOGO_LIGHT} 
          alt="Diametor" 
          className="h-8"
          data-testid="profile-logo"
        />
      </div>

      <div className="max-w-lg mx-auto p-6 md:p-8">
        <h1 className={`font-outfit text-2xl sm:text-3xl font-bold tracking-tight mb-1 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>
          Complete Your Profile
        </h1>
        <p className={`font-ibm text-sm mb-8 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
          Set up your account to start managing debts
        </p>

        {/* Profile Photo */}
        <div className="flex justify-center mb-8">
          <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()} data-testid="photo-upload-area">
            <div className={`w-24 h-24 rounded-full overflow-hidden border-2 ${isDark ? 'border-zinc-700' : 'border-zinc-300'} flex items-center justify-center ${isDark ? 'bg-zinc-900' : 'bg-zinc-100'}`}>
              {photoPreview ? (
                <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <Camera size={32} className={isDark ? 'text-zinc-600' : 'text-zinc-400'} />
              )}
            </div>
            <div className="absolute bottom-0 right-0 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
              <Camera size={14} weight="bold" className="text-zinc-950" />
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" data-testid="photo-file-input" />
          </div>
        </div>

        {/* Name */}
        <div className="mb-5">
          <label className={`font-ibm text-xs font-semibold uppercase tracking-[0.2em] mb-2 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
            Full Name *
          </label>
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Your full name"
            className={`h-11 rounded-sm font-ibm ${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-50 focus:ring-yellow-400' : 'bg-zinc-50 border-zinc-200 text-zinc-950 focus:ring-yellow-400'}`}
            data-testid="name-input"
          />
        </div>

        {/* Email (read-only) */}
        <div className="mb-5">
          <label className={`font-ibm text-xs font-semibold uppercase tracking-[0.2em] mb-2 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
            Email
          </label>
          <Input
            value={user?.email || ''}
            readOnly
            className={`h-11 rounded-sm font-ibm opacity-60 ${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`}
            data-testid="email-input"
          />
        </div>

        {/* Phone with Country Code */}
        <div className="mb-5">
          <label className={`font-ibm text-xs font-semibold uppercase tracking-[0.2em] mb-2 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
            Phone Number *
          </label>
          <div className="flex gap-2">
            <Select value={countryCode} onValueChange={setCountryCode}>
              <SelectTrigger 
                className={`w-32 h-11 rounded-sm font-ibm ${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`}
                data-testid="country-code-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={isDark ? 'bg-zinc-900 border-zinc-800' : ''}>
                {COUNTRY_CODES.map(cc => (
                  <SelectItem key={cc.code} value={cc.code} className="font-ibm text-sm">
                    {cc.code} {cc.country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={phone}
              onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
              placeholder="Phone number"
              className={`flex-1 h-11 rounded-sm font-ibm ${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`}
              data-testid="phone-input"
            />
          </div>
          <p className={`font-ibm text-[10px] mt-1.5 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
            Phone will be verified manually by our team
          </p>
        </div>

        {/* Currency */}
        <div className="mb-8">
          <label className={`font-ibm text-xs font-semibold uppercase tracking-[0.2em] mb-2 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
            Currency *
          </label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger 
              className={`w-full h-11 rounded-sm font-ibm ${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`}
              data-testid="currency-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={isDark ? 'bg-zinc-900 border-zinc-800' : ''}>
              {CURRENCIES.map(c => (
                <SelectItem key={c.code} value={c.code} disabled={!c.enabled} className={`font-ibm text-sm ${!c.enabled ? 'opacity-40' : ''}`}>
                  {c.symbol} {c.label}{!c.enabled ? ' (Coming soon)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="w-full h-12 bg-yellow-400 text-zinc-950 font-ibm font-semibold text-sm rounded-sm hover:bg-yellow-500 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="complete-profile-button"
        >
          {saving ? (
            <div className="animate-spin w-5 h-5 border-2 border-zinc-950 border-t-transparent rounded-full" />
          ) : (
            <>
              Complete Profile
              <ArrowRight size={16} weight="bold" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
